import React, { Component } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import { apiErrorMessage } from "Helpers/ContentValidation";

import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Chip, Grid, IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

import {
  DragHandle,
  beginDrag,
  readDrag,
  endDrag,
  moveItem,
} from "components/ContentForms/DragList";

// Rows live inside DataTable, which does not expose the <tr> to us. Rather than
// fork it, each row tags itself with its product id and the drop is handled on
// the table wrapper — so the whole row is a drop target, and searching or
// paginating cannot shift which product a drop lands on.
const ROW_ID_ATTRIBUTE = "data-product-id";
const DRAG_GROUP = "brand-products";

/**
 * Products that belong to one of our own brands or ranges.
 *
 * Separate from the existing "Products" screen, which drives the category-based
 * Products page and is unchanged.
 */
export default class BrandProducts extends Component {
  state = {
    products: [],
    brandOptions: [],
    // Pre-selected when arriving from the brand list ("Manage products").
    brandFilter: new URLSearchParams(window.location.search).get("brand") || "",
    statusFilter: "",

    loading: false,
    reordering: false,
    // Product currently being dragged, so its row can dim while it moves.
    dragId: null,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchBrandOptions();
    this.fetchProducts();
  }

  /* =============================
     FETCH
  ============================= */
  fetchBrandOptions = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.get(Constants.getUrls.getBrandOptions);
      this.setState({ brandOptions: response.data?.data || [] });
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  fetchProducts = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true });

    const params = new URLSearchParams({ limit: "500" });
    if (this.state.brandFilter) params.set("brand", this.state.brandFilter);
    if (this.state.statusFilter) params.set("status", this.state.statusFilter);

    try {
      const response = await axios.get(
        `${Constants.getUrls.getAllBrandProducts}?${params.toString()}`
      );
      this.setState({ products: response.data?.data?.docs || [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not load products"));
    }
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /* =============================
     FILTERS
  ============================= */
  handleFilterChange = (field, value) => {
    this.setState({ [field]: value }, this.fetchProducts);
  };

  /* =============================
     ORDER

     Only offered while a single brand is selected — sort_order is per brand,
     so swapping rows across brands would be meaningless.
  ============================= */
  handleMove = async (index, direction) => {
    const { products } = this.state;
    const target = index + direction;
    if (target < 0 || target >= products.length) return;

    if (init() !== "success") return this.setState({ redirect: true });

    const moved = products[index];
    const swapped = products[target];

    this.setState({ reordering: true });

    try {
      await axios.put(Constants.putUrls.reorderBrandProducts, {
        items: [
          { id: moved._id, sort_order: target },
          { id: swapped._id, sort_order: index },
        ],
      });

      this.setState({ reordering: false });
      this.fetchProducts();
    } catch (error) {
      this.setState({ reordering: false });
      this.notify("warning", apiErrorMessage(error, "Could not change the order"));
    }
  };

  /**
   * Drops a product at an arbitrary position. Unlike the arrows, this shifts
   * every row in between, so the whole list is renumbered and sent at once —
   * the endpoint takes up to 500 entries in one call.
   */
  handleDropReorder = async (fromId, toId) => {
    const { products } = this.state;
    const from = products.findIndex((product) => product._id === fromId);
    const to = products.findIndex((product) => product._id === toId);

    if (from < 0 || to < 0 || from === to) return;
    if (init() !== "success") return this.setState({ redirect: true });

    const reordered = moveItem(products, from, to);

    // Show the new order immediately; the refetch below confirms it.
    this.setState({ products: reordered, reordering: true });

    try {
      await axios.put(Constants.putUrls.reorderBrandProducts, {
        items: reordered.map((product, index) => ({
          id: product._id,
          sort_order: index,
        })),
      });

      this.setState({ reordering: false });
      this.fetchProducts();
    } catch (error) {
      // Put the old order back, since the server never accepted the new one.
      this.setState({ products, reordering: false });
      this.notify("warning", apiErrorMessage(error, "Could not change the order"));
    }
  };

  /* Drop handling for the table wrapper — resolves the row under the pointer. */
  rowIdFromEvent = (event) => {
    const row = event.target.closest?.("tr");
    return row?.querySelector(`[${ROW_ID_ATTRIBUTE}]`)?.getAttribute(ROW_ID_ATTRIBUTE) || null;
  };

  handleTableDragOver = (event) => {
    if (readDrag(DRAG_GROUP) === null) return;
    if (this.rowIdFromEvent(event)) event.preventDefault();
  };

  handleTableDrop = (event) => {
    const draggedIndex = readDrag(DRAG_GROUP);
    if (draggedIndex === null) return;

    const targetId = this.rowIdFromEvent(event);
    const fromId = this.state.products[draggedIndex]?._id;
    endDrag();
    this.setState({ dragId: null });

    if (targetId && fromId) {
      event.preventDefault();
      this.handleDropReorder(fromId, targetId);
    }
  };

  /* =============================
     STATUS / DELETE
  ============================= */
  handleToggleStatus = async (product) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.patch(
        Constants.patchUrls.toggleBrandProductStatus + product._id
      );
      this.notify("success", response.data?.message || "Status updated");
      this.fetchProducts();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not change the status"));
    }
  };

  handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.delete(
        Constants.deleteUrls.deleteBrandProduct + product._id
      );
      this.notify("success", response.data?.message || "Product deleted");
      this.fetchProducts();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not delete the product"));
    }
  };

  /* =============================
     RENDER
  ============================= */
  render() {
    const { products, brandOptions, brandFilter, statusFilter, loading, redirect } =
      this.state;

    if (redirect) return <Navigate to="/signin" replace />;

    const canReorder = Boolean(brandFilter) && !statusFilter;
    const addLink = brandFilter
      ? `/addbrandproduct?brand=${brandFilter}`
      : "/addbrandproduct";

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Brand Products
              </MDTypography>
              <MDTypography variant="caption" color="white">
                Product ranges for our own brands — pack sizes, variants and images
              </MDTypography>
            </MDBox>

            <MDBox px={2} pt={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" fontWeight="medium" display="block">
                    Brand
                  </MDTypography>
                  <Select
                    fullWidth
                    displayEmpty
                    // A brand arriving from the URL is set before the options
                    // finish loading; showing it early makes MUI warn about a
                    // value with no matching option.
                    value={brandOptions.length ? brandFilter : ""}
                    onChange={(event) =>
                      this.handleFilterChange("brandFilter", event.target.value)
                    }
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">All brands</MenuItem>
                    {brandOptions.map((option) => (
                      <MenuItem key={option._id} value={option._id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <MDTypography variant="caption" fontWeight="medium" display="block">
                    Status
                  </MDTypography>
                  <Select
                    fullWidth
                    displayEmpty
                    value={statusFilter}
                    onChange={(event) =>
                      this.handleFilterChange("statusFilter", event.target.value)
                    }
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </Grid>

                <Grid item xs={12} sm={5} align="right">
                  <Link to={addLink}>
                    <MDButton variant="contained" size="small" color="primary">
                      Add Brand Product
                    </MDButton>
                  </Link>
                </Grid>
              </Grid>

              {canReorder ? (
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Drag a row by its grip onto any other row to move it there, or
                  use the arrows to nudge it one place.
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Pick a single brand (with no status filter) to reorder its products.
                </MDTypography>
              )}
            </MDBox>

            <MDBox
              py={3}
              onDragOver={canReorder ? this.handleTableDragOver : undefined}
              onDrop={canReorder ? this.handleTableDrop : undefined}
            >
              {loading ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : products.length > 0 ? (
                <DataTable
                  canSearch
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50, 100] }}
                  table={{
                    columns: [
                      { Header: "Order", accessor: "order", width: "8%" },
                      { Header: "Image", accessor: "image" },
                      { Header: "Product", accessor: "product" },
                      { Header: "Pack size", accessor: "pack" },
                      { Header: "Brand", accessor: "brand" },
                      { Header: "Status", accessor: "status" },
                      { Header: "Actions", accessor: "action" },
                    ],
                    rows: products.map((product, index) => ({
                      order: canReorder ? (
                        <MDBox
                          display="flex"
                          alignItems="center"
                          {...{ [ROW_ID_ATTRIBUTE]: product._id }}
                          style={{
                            opacity: this.state.dragId === product._id ? 0.4 : 1,
                          }}
                        >
                          <DragHandle
                            label="Drag to reorder"
                            disabled={this.state.reordering}
                            draggable={!this.state.reordering}
                            onDragStart={(event) => {
                              beginDrag(DRAG_GROUP, index);
                              this.setState({ dragId: product._id });
                              event.dataTransfer.effectAllowed = "move";
                              event.dataTransfer.setData("text/plain", product._id);
                            }}
                            onDragEnd={() => {
                              endDrag();
                              this.setState({ dragId: null });
                            }}
                          />

                          {/* Spans are required: a disabled button fires no
                              events, so Tooltip cannot listen to it directly. */}
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === 0 || this.state.reordering}
                                onClick={() => this.handleMove(index, -1)}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  index === products.length - 1 || this.state.reordering
                                }
                                onClick={() => this.handleMove(index, 1)}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </MDBox>
                      ) : (
                        <MDTypography variant="caption">
                          {product.sort_order ?? 0}
                        </MDTypography>
                      ),

                      image: product.image ? (
                        <img
                          src={
                            product.image.startsWith("/uploads/")
                              ? Constants.imageDomain + product.image
                              : product.image
                          }
                          alt={product.name}
                          style={{
                            height: 56,
                            width: 56,
                            objectFit: "contain",
                            borderRadius: 6,
                            background: "#fafafa",
                          }}
                        />
                      ) : (
                        "—"
                      ),

                      product: (
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {product.name}
                          </MDTypography>
                          {product.variant ? (
                            <MDTypography variant="caption" color="text">
                              {product.variant}
                            </MDTypography>
                          ) : null}
                        </MDBox>
                      ),

                      pack: product.pack_size || "—",

                      brand: product.brand?.name || "—",

                      status: (
                        <Chip
                          size="small"
                          label={product.status === "active" ? "Active" : "Inactive"}
                          color={product.status === "active" ? "success" : "default"}
                        />
                      ),

                      action: (
                        <div style={{ display: "flex", gap: 6 }}>
                          <Tooltip title="Edit product">
                            <Link to={`/updatebrandproduct/${product._id}`}>
                              <IconButton size="small" color="info">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip
                            title={
                              product.status === "active"
                                ? "Hide from the website"
                                : "Show on the website"
                            }
                          >
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => this.handleToggleStatus(product)}
                            >
                              {product.status === "active" ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete product">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => this.handleDelete(product)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ),
                    })),
                  }}
                />
              ) : (
                <div style={{ padding: 20 }}>No Brand Products Found!</div>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Products"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
